// Central adapter between the (unmodified) dashboard testing engine and Supabase.
// This is the only place in the app that talks to the `test_attempts` table, so the
// engine's own save/clear/export call sites (see public/vendor-app-shell.js) never touch
// Supabase directly — they just call the methods below via window.__APPS2027_STORAGE_ADAPTER__.

import { reportError } from '../monitoring.js';

const QUANT_KEYS = new Set(['mental', 'probability', 'numberlogic', 'likelihood', 'intervals', 'orderbooks', 'zap']);

function domainForModuleKey(moduleKey) {
  return QUANT_KEYS.has(moduleKey) ? 'quant' : 'finance';
}

function pendingQueueKey(userId) {
  return `apps2027.pendingSync.${userId}`;
}

function readPendingQueue(userId) {
  try {
    const raw = localStorage.getItem(pendingQueueKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writePendingQueue(userId, queue) {
  try {
    localStorage.setItem(pendingQueueKey(userId), JSON.stringify(queue));
  } catch {
    // best-effort only — losing the pending-sync cache just means a retry won't happen
  }
}

function rowToEntry(row) {
  return row.payload;
}

export function createStorageAdapter({ supabase, userId }) {
  async function saveAttemptNow(entry) {
    const row = {
      user_id: userId,
      client_attempt_id: entry.id,
      module_key: entry.section,
      domain: domainForModuleKey(entry.section),
      score: entry.score,
      duration_sec: entry.durationSec,
      completed_at: entry.completedAt,
      completion_reason: entry.completionReason || null,
      payload: entry,
    };
    const { error } = await supabase
      .from('test_attempts')
      .upsert(row, { onConflict: 'user_id,client_attempt_id' });
    if (error) {
      reportError(error, { op: 'saveAttempt' });
      throw error;
    }
  }

  return {
    async fetchHistory() {
      const { data, error } = await supabase
        .from('test_attempts')
        .select('payload')
        .order('completed_at', { ascending: false })
        .limit(250);
      if (error) {
        reportError(error, { op: 'fetchHistory' });
        throw error;
      }
      return (data || []).map(rowToEntry);
    },

    async saveAttempt(entry) {
      try {
        await saveAttemptNow(entry);
      } catch (err) {
        const queue = readPendingQueue(userId);
        queue.push(entry);
        writePendingQueue(userId, queue.slice(-50));
        throw err;
      }
    },

    async flushPending() {
      const queue = readPendingQueue(userId);
      if (!queue.length) return;
      const remaining = [];
      for (const entry of queue) {
        try {
          await saveAttemptNow(entry);
        } catch {
          remaining.push(entry);
        }
      }
      writePendingQueue(userId, remaining);
    },

    async deleteDomain(domain) {
      const { error } = await supabase.from('test_attempts').delete().eq('domain', domain);
      if (error) {
        reportError(error, { op: 'deleteDomain', domain });
        throw error;
      }
    },

    async deleteAll() {
      const { error } = await supabase.from('test_attempts').delete().eq('user_id', userId);
      if (error) {
        reportError(error, { op: 'deleteAll' });
        throw error;
      }
    },

    async recordDailyActivity() {
      const today = new Date().toISOString().slice(0, 10);
      const { error: activityError } = await supabase
        .from('user_daily_activity')
        .upsert({ user_id: userId, activity_date: today }, { onConflict: 'user_id,activity_date', ignoreDuplicates: true });
      if (activityError) reportError(activityError, { op: 'recordDailyActivity:upsert' });

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ last_active_at: new Date().toISOString() })
        .eq('user_id', userId);
      if (profileError) reportError(profileError, { op: 'recordDailyActivity:updateProfile' });
    },
  };
}
